<template>
    
    <div class="atd-for-sm-list" id="atd-for-sm-list">
        <div class="ai-sidebar" id="aiExamples">
            <div ref="progressTooltip" class="rlms-w-fc" style="display: none;">
                <span>tippy</span>
                <div ref="tooltipContent">
                    Lorem ipsum dolor sit amet, consectetur adipisicing elit. Aperiam voluptates blanditiis consequatur, cumque ipsam similique delectus quaerat quisquam ipsa distinctio atque commodi praesentium adipisci aliquid quasi laboriosam possimus, laudantium perspiciatis?
                </div>
            </div>
            <div class="ai-sidebar-head">
                <span>AI Examples</span>
                <span class="material-symbols-outlined ai-s-close-btn">close</span>
            </div>
            <div class="ai-sidebar-body">
                <div class="ai-sidebar-no-data" v-if="!syllable || !consonants || !vowel">
                    <img src="@/assets/images/ai-assistant.png" width="80">
                    <span class="rlms-fs-xxs">Please complete the assessment test to get the AI generated
                        results.</span>
                </div>
                <template v-else>
                    <div class="ai-sidebar-no-data" v-if="loading">
                        <img :src="aiImg" width="150">
                        <span class="rlms-fs-xxs">Give some time while we are searching some examples for you!</span>
                    </div>

                    <template v-else>
                        <template v-if="examples?.length > 0">
                            <p class="rlms-fs-xs">Give some time while we are searching some examples for you!</p>
                            <p class="rlms-fs-xs rlms-fw-500 rlms-my-4">Showing AI generated {{ examples?.length }}
                                examples.</p>
                            <ul class="ai-sidebar-data-list">
                                <li v-for="(example, index) in examples" :key="index" @click="toggleSelection(index)"
                                    :class="{ active: selectedIndexes.includes(index), disabled: isSelected(example) }">
                                    <div class="rlms-flex rlms-flex-col rlms-gap-5">
                                        <div class="rlms-fw-500 rlms-fs-xxs"> {{ index + 1 }}. {{ example.word }} {{
                                            example.ipa }}</div>
                                        <p class="rlms-fs-xxs">Consonants: {{ example.consonants.join(', ') }}</p>
                                        <p class="rlms-fs-xxs">Vowels: {{ example.vowels.join(', ') }}</p>
                                        <div class="source-tags" v-if="example.source">
                                            <span v-for="tag in getSourceTags(example.source)" :key="tag"
                                                class="source-tag" :class="getSourceClass(tag)"
                                                :title="getSourceTooltip(tag)">
                                                {{ getSourceLabel(tag) }}
                                            </span>
                                        </div>
                                    </div>
                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M14.6673 11.8276V15.6776C14.6673 18.8859 13.384 20.1693 10.1757 20.1693H6.32565C3.11732 20.1693 1.83398 18.8859 1.83398 15.6776V11.8276C1.83398 8.61927 3.11732 7.33594 6.32565 7.33594H10.1757C13.384 7.33594 14.6673 8.61927 14.6673 11.8276Z"
                                            fill="#725CFF" stroke="#725CFF" stroke-width="1.5" stroke-linecap="round"
                                            stroke-linejoin="round" />
                                        <path
                                            d="M20.1673 6.3276V10.1776C20.1673 13.3859 18.884 14.6693 15.6757 14.6693H14.6673V11.8276C14.6673 8.61927 13.384 7.33594 10.1757 7.33594H7.33398V6.3276C7.33398 3.11927 8.61732 1.83594 11.8257 1.83594H15.6757C18.884 1.83594 20.1673 3.11927 20.1673 6.3276Z"
                                            stroke="#725CFF" stroke-width="1.5" stroke-linecap="round"
                                            stroke-linejoin="round" />
                                    </svg>
                                </li>
                            </ul>
                        </template>
                        <template v-else>
                            <div class="ai-sidebar-no-data">
                                <img src="@/assets/images/ai-assistant.png" width="80">
                                <span class="rlms-fs-xxs">Generate some targets via our AI! Please click generate to
                                    start.</span>
                                <div class="rlms-py-2 rlms-px-3 rlms-br-8 rlms-w-fc rlms-bg-primary-500 rlms-fs-xxs white rlms-text-center rlms-text-white rlms-fw-500 rlms-cursor-pointer"
                                    @click="getVerbalTargets">
                                    GENERATE
                                </div>
                            </div>
                        </template>
                    </template>
                </template>


                <div class="ai-sidebar-no-data" v-if="false">
                    <img src="@/assets/images/ai-assistant.png" width="80">
                    <span class="rlms-fs-xxs">Generate some targets via our AI! Please click generate to start...</span>
                    <div class="rlms-py-2 rlms-px-3 rlms-br-8 rlms-w-fc rlms-bg-primary-500 rlms-fs-xxs white rlms-text-center rlms-text-white rlms-fw-500 rlms-cursor-pointer"
                        @click="getVerbalTargets">
                        GENERATE</div>
                </div>
            </div>
            <div class="ai-sidebar-footer" v-if="examples?.length > 0">
                <button class="rlms-btn rlms-btn-primary rlms-w-100" @click="selectItem"
                    :disabled="!selectedIndexes?.length">
                    Add Selected</button>
            </div>
        </div>
    </div>
    
</template>

<script setup>
import { onMounted, ref, watch, computed ,nextTick } from 'vue';
import aiImg from '@/assets/images/ai-robot-img.png';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import { aiFeaturesAllowed } from '@/utils/legal-gate';
import { showToast } from '@/utils/swal';
import { boolean } from 'yup';
import Swal from 'sweetalert2';
import tippy from 'tippy.js';

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);
const props = defineProps({
    syllable: Number,
    consonants: Number,
    vowel: Number,
    preferences_list: Object,
    selectedItems: Array,
    loadExample: boolean,
    known_syllables: Array,
});

const showModal = ref(true);
const loading = ref(false);
const examples = ref([]);
const selectedIndexes = ref([]);
const progressTooltip = ref(null);
const tooltipContent = ref(null);

// Check if data is missing
const hasKnownSyllables = computed(() => props.known_syllables && props.known_syllables.length > 0);
const hasPreferences = computed(() => {
    const pl = props.preferences_list;
    return pl && (pl.activities || pl.items || pl.places);
});

// Source tag helpers
const getSourceLabel = (source) => {
    if (Array.isArray(source)) {
        return source.map(s => getSourceLabel(s)).join(' + ');
    }
    const labels = {
        'syllable_tracker': 'Syllable Tracker',
        'preferences': 'Preferences',
        'scv_based': 'SCV Based'
    };
    return labels[source] || source;
};

const getSourceTooltip = (source) => {
    if (Array.isArray(source)) {
        return source.map(s => getSourceTooltip(s)).join('. ');
    }
    const tooltips = {
        'syllable_tracker': 'Uses sounds the learner has demonstrated',
        'preferences': 'Related to learner interests',
        'scv_based': 'Matches syllable complexity level'
    };
    return tooltips[source] || '';
};

const getSourceClass = (source) => {
    if (Array.isArray(source)) {
        // For multiple sources, use the first one for the main class
        return getSourceClass(source[0]);
    }
    return {
        'tag-syllable': source === 'syllable_tracker',
        'tag-preferences': source === 'preferences',
        'tag-scv': source === 'scv_based'
    };
};

const getSourceTags = (source) => {
    if (Array.isArray(source)) {
        return source;
    }
    return [source];
};

const handleModal = (action = 'close') => {
    showModal.value = false;
    emit(action);
};

const getVerbalTargets = async () => {
    if (!props.syllable || !props.consonants || !props.vowel) return;
    if (!aiFeaturesAllowed()) return;

    // Build warning message if data is missing
    const warnings = [];
    if (!hasKnownSyllables.value) {
        warnings.push('<b>Syllable Tracker:</b> Remember to mark the syllables your learner has completed. See Syllable Tracker tab.');
    }
    if (!hasPreferences.value) {
        warnings.push('<b>Preferences List:</b> Adding preferences helps the AI suggest more relevant words.');
    }

    // Show warning if any data is missing
    if (warnings.length > 0) {
        const result = await Swal.fire({
            title: 'Missing Data',
            html: `<div style="text-align: left; font-size: 14px;">
                <p style="margin-bottom: 10px;">The following data is missing:</p>
                <ul style="padding-left: 20px;">
                    ${warnings.map(w => `<li style="margin-bottom: 8px;">${w}</li>`).join('')}
                </ul>
                <p style="margin-top: 10px;">You can still generate words, but results may be less personalized.</p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Continue Anyway',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#725CFF',
        });

        if (!result.isConfirmed) {
            return;
        }
    }

    selectedIndexes.value = [];

    loading.value = true
    try {
        const { data } = await api.post("/open-ai/ipa/generate", {
            syllable: props.syllable,
            consonants: props.consonants,
            vowel: props.vowel,
            activities: props.preferences_list?.activities,
            items: props.preferences_list?.items,
            people_places: props.preferences_list?.places,
            known_syllables: props.known_syllables || []
        });

        examples.value = data?.results || [];
    } catch (error) {
        handleError(error);
    } finally {
        loading.value = false;
    }
}

// const copyToClipboard = (example) => {
//     const text = `${example.word} ${example.ipa}\nConsonants: ${example.consonants.join(', ')}\nVowels: ${example.vowels.join(', ')}`
//     navigator.clipboard.writeText(text);

//     showToast({ icon: 'success', text: 'Copied to clipboard!' })
// }

const selectItem = () => {
    if (selectedIndexes.value.length === 0) {
        showToast({ icon: 'warning', text: 'Target already exist!' })

        return;
    }

    const selectedExamples = selectedIndexes.value.map(i => examples.value[i]);

    // Prepare JSON payload
    const payload = selectedExamples.map(ex => ({
        word: ex.word,
        ipa: ex.ipa,
        consonants: ex.consonants,
        vowels: ex.vowels,
    }));

    emit('saved', payload); // emit as JSON
    handleModal('close');

    selectedIndexes.value = [];
}

const toggleSelection = (index) => {
    const idx = selectedIndexes.value.indexOf(index);

    if (idx === -1) {
        // Add index if not already selected
        selectedIndexes.value.push(index);
    } else {
        // Remove index if already selected
        selectedIndexes.value.splice(idx, 1);
    }
};

const isSelected = (item) => {
    return props.selectedItems.some(i => i.word === item.word);
};

// onMounted(() => {
//     if (props.syllable && props.consonants && props.vowel) {
//         // getVerbalTargets()
//     }
// });

onMounted(async () => {
    await nextTick();
    if (!progressTooltip.value || !tooltipContent.value) return;

    tippy(progressTooltip.value, {
        content: tooltipContent.value,
        // trigger: 'click',
        placement: 'bottom',
        allowHTML: true,
        interactive: true,
        onShow(instance) {
            instance.popper.classList.add('eesa-default-tooltip');
        },
    });
});

watch(
    () => props.loadExample,
    (newValue) => {
        if (newValue) {
            getVerbalTargets();
        }
    }
);
</script>

<style scoped>
.source-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
}

.source-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    cursor: help;
}

.tag-syllable {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.tag-preferences {
    background-color: #e3f2fd;
    color: #1565c0;
}

.tag-scv {
    background-color: #f3e5f5;
    color: #7b1fa2;
}
</style>
